import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useHttpClient } from '@bonusly/http-client';

import { latestCheckInQueryKey, meetingCheckInsQueryKey } from '../../../../../queries/check-ins';
import { EventAPIData, MeetingAPIData, UserAPIData } from '../../../types/growth-api';
import { CheckInAPIData } from '../../../../../queries/check-ins';
import { QueryKey } from '../../../types/growth-query-key';
import { EventDTO } from '../lib/entities/event';
import { MeetingDTO } from '../lib/entities/meeting';
import { UserDTO } from '../lib/entities/user';
import { useMeetingWithSocket } from './use-meeting-with-socket';

const DELAY_FOR_REFETCH = 30000;

const stringify = (obj: any): string => {
	return JSON.stringify(obj, Object.keys(obj).sort());
};

const cachedUsers = new Map<string, UserDTO>();
const cachedEvents = new Map<string, EventDTO>();

export const useOneOnOneMeeting = (meetingId: string | undefined, reloading: number | boolean = true) => {
	const http = useHttpClient();
	const meetingStateRef = useRef({
		string: '',
		data: null as MeetingAPIData | null,
		dto: null as MeetingDTO | null,
	});

	const queryResult = useQuery({
		queryKey: [QueryKey.Meeting, meetingId],
		queryFn: async () => {
			const meetingRes = await http.get<MeetingAPIData>(`/one_on_ones/meetings/${meetingId}`);
			const meetingData = meetingRes.data;
			const currentString = meetingStateRef.current.string;
			const fetchedString = stringify(meetingData);
			const newMeeting = currentString !== fetchedString;

			if (newMeeting) {
				meetingStateRef.current.string = fetchedString;
				meetingStateRef.current.data = meetingData;
				meetingStateRef.current.dto = new MeetingDTO(meetingData);
			}

			const dto = meetingStateRef.current.dto;

			if (newMeeting) {
				let guest, host, event;
				guest = cachedUsers.get(meetingData.guest_id);
				host = cachedUsers.get(meetingData.host_id);
				event = cachedEvents.get(meetingData.event_id);

				if (!guest || !host || !event) {
					const guestPromise = guest ? null : http.get<UserAPIData>(`/user_management/users/${meetingData.guest_id}`);
					const hostPromise = host ? null : http.get<UserAPIData>(`/user_management/users/${meetingData.host_id}`);
					const eventPromise = event ? null : http.get<EventAPIData>(`/one_on_ones/events/${meetingData.event_id}`);
					const [guestRes, hostRes, eventRes] = await Promise.all([guestPromise, hostPromise, eventPromise]);
					if (guestRes) cachedUsers.set(meetingData.guest_id, (guest = new UserDTO(guestRes.data)));
					if (hostRes) cachedUsers.set(meetingData.host_id, (host = new UserDTO(hostRes.data)));
					if (eventRes) cachedEvents.set(meetingData.event_id, (event = new EventDTO(eventRes.data)));
				}

				guest = cachedUsers.get(meetingData.guest_id);
				host = cachedUsers.get(meetingData.host_id);
				event = cachedEvents.get(meetingData.event_id);

				if (dto && guest && host && event) {
					dto.event = event;
					dto.event.host = host;
					dto.event.guest = guest;
				}
			}

			return dto;
		},
		enabled: !!meetingId,
		refetchInterval: reloading === false ? false :reloading === true ? DELAY_FOR_REFETCH : Number(reloading) || DELAY_FOR_REFETCH
	});

	return queryResult;
};

export const useLiveMeeting = (meetingId: string | undefined) => {
	return useMeetingWithSocket(meetingId || '');
};

interface UpdateOneOnOneMeetingCheckinParams {
	score?: number;
	message?: string;
}

export const useUpdateOneOnOneMeetingCheckin = (meetingId: string) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(async (checkIn: UpdateOneOnOneMeetingCheckinParams): Promise<CheckInAPIData> => {
		const res = await http.post<CheckInAPIData>(`/one_on_ones/meetings/${meetingId}/check_ins`, checkIn);
		queryClient.invalidateQueries([QueryKey.Meeting, meetingId]);
		queryClient.invalidateQueries({ queryKey: meetingCheckInsQueryKey(meetingId) });
		queryClient.invalidateQueries({ queryKey: latestCheckInQueryKey() });
		return res.data;
	});
};

export const useUpdateEditedAgenda = (meetingId: string) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (editedAgenda: string) => {
			const response = await http.patch(`/meeting_context/${meetingId}/update_edited_agenda`, {
				edited_agenda: editedAgenda,
			});
			return response.data;
		},
		onSuccess: () => {
			// Invalidate and refetch the meeting data to show the updated edited agenda
			queryClient.invalidateQueries({ queryKey: [QueryKey.Meeting, meetingId] });
		},
		onError: (error) => {
			console.error('Failed to update edited agenda:', error);
		},
	});
};

export const useUpdateEditedBrief = (meetingId: string) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (editedBrief: string) => {
			const response = await http.patch(`/meeting_context/${meetingId}/update_edited_brief`, {
				edited_brief: editedBrief,
			});
			return response.data;
		},
		onSuccess: () => {
			// Invalidate and refetch the meeting data to show the updated edited brief
			queryClient.invalidateQueries({ queryKey: [QueryKey.Meeting, meetingId] });
		},
		onError: (error) => {
			console.error('Failed to update edited brief:', error);
		},
	});
};
